import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Id, toast } from 'react-toastify'

import { bodyMeasurementsApi } from '../api/services/dd_panel_body_measurements'
import { bodyMeasurementKeys } from '../api/services/dd_panel_body_measurements/queryKeys.ts'
import { AddMeasurementData } from '../api/services/dd_panel_body_measurements/types.ts'

type MutationToastContext = {
  toastId: Id
}

type UseBodyMeasurementMutationsParams = {
  onSuccess?: () => void
}

type EditMeasurementVariables = {
  measurementId: number
  measurementData: AddMeasurementData
}

export const useBodyMeasurementMutations = ({
  onSuccess
}: UseBodyMeasurementMutationsParams = {}) => {
  const queryClient = useQueryClient()

  const showLoadingToast = (message: string): MutationToastContext => ({
    toastId: toast.loading(message)
  })

  const updateToast = (
    toastId: Id,
    message: string | null | undefined,
    type: 'success' | 'error'
  ) => {
    toast.update(toastId, {
      render: message,
      type,
      delay: 500,
      isLoading: false,
      autoClose: 2000
    })
  }

  const handleMutationSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: bodyMeasurementKeys.all,
      refetchType: 'active'
    })

    onSuccess?.()
  }

  const handleMutationError = (error: Error) => {
    console.error('Error updating body measurement:', error)
  }

  const addMeasurementMutation = useMutation({
    mutationFn: (measurementData: AddMeasurementData) =>
      bodyMeasurementsApi.addMeasurement(measurementData),

    onMutate: () => showLoadingToast('Rozpoczęto dodawanie nowego pomiaru...'),

    onSuccess: async (response, _variables, context) => {
      const isSuccess = response.status === 201
      const isError = response.status === 400

      isError && toast.dismiss(context.toastId)

      isSuccess &&
        updateToast(context.toastId, 'Dodawanie pomiaru zakończone', 'success')

      if (isSuccess) {
        await handleMutationSuccess()
      }
    },

    onError: (error, _variables, context) => {
      if (context) {
        updateToast(context.toastId, 'Nie udało się dodać pomiaru', 'error')
      }

      handleMutationError(error)
    }
  })

  const removeMeasurementMutation = useMutation({
    mutationFn: (measurementId: number) =>
      bodyMeasurementsApi.removeMeasurement(measurementId),
    onMutate: () => showLoadingToast('Rozpoczęto usuwanie pomiaru...'),

    onSuccess: async (response, _variables, context) => {
      const isSuccess = response.status === 204

      updateToast(
        context.toastId,
        isSuccess ? 'Usuwanie pomiaru zakończone' : response.error,
        isSuccess ? 'success' : 'error'
      )

      if (isSuccess) {
        await handleMutationSuccess()
      }
    },
    onError: (error, _variables, context) => {
      if (context) {
        updateToast(context.toastId, 'Nie udało się usunąć pomiaru', 'error')
      }

      handleMutationError(error)
    }
  })

  const editMeasurementMutation = useMutation({
    mutationFn: ({
      measurementId,
      measurementData
    }: EditMeasurementVariables) =>
      bodyMeasurementsApi.editMeasurement(measurementId, measurementData),

    onMutate: () => showLoadingToast('Rozpoczęto zapisywanie zmian pomiaru...'),

    onSuccess: async (response, _variables, context) => {
      const isSuccess = response.status === 200
      const isError = response.status === 400

      isSuccess && updateToast(
        context.toastId,
        'Edycja pomiaru zakończona',
        'success'
      )

      isError && toast.dismiss(context.toastId)

      if (isSuccess) {
        await handleMutationSuccess()
      }
    },

    onError: (error, _variables, context) => {
      if (context) {
        toast.dismiss(context.toastId)
      }

      handleMutationError(error)
    }
  })

  return {
    addMeasurementMutation,
    removeMeasurementMutation,
    editMeasurementMutation
  }
}
